/**
 * m3 echo Frontend - Currency Formatting Helpers
 *
 * Money that comes back from the API is already converted: the backend prices
 * every token-usage record in USD, converts it exactly once into its reporting
 * currency and states which one that is (`currency` / `fx` in the response, or
 * the `currency` field on a tenant row).
 *
 * The rule that follows from this is the whole point of this module: a screen
 * renders the currency the response names, never a hard-coded symbol. Three
 * admin views used to print "$" over values the backend had already converted
 * into euro - a wrong number on screen, not a cosmetic slip.
 */

/** Display symbols for the currencies the backend can report in. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  CHF: 'CHF',
};

/**
 * Fallback for responses that predate the currency field.
 *
 * Deliberately the backend's reporting currency rather than USD: an older
 * payload without the field carries values that were converted all the same.
 */
export const DEFAULT_CURRENCY = 'EUR';

/** Symbol for a currency code, falling back to the code itself. */
export const currencySymbol = (code: string | undefined | null): string => {
  const normalized = (code || DEFAULT_CURRENCY).toUpperCase();
  return CURRENCY_SYMBOLS[normalized] ?? normalized;
};

/** Format an amount with the symbol of the currency the API reported. */
export const formatMoney = (
  value: number | null | undefined,
  code: string | undefined | null,
  digits = 2
): string => `${(value || 0).toFixed(digits)} ${currencySymbol(code)}`;
